import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { agendaApi } from '../../api/endpoints.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import Card from '../../components/Card.jsx';
import Spinner from '../../components/Spinner.jsx';
import Badge from '../../components/Badge.jsx';
import { Field, Select, TextInput } from '../../components/Field.jsx';
import { formatDate, isoDay } from '../../utils/format.js';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
const TIMEZONES = ['Europe/Paris', 'Asia/Jerusalem', 'Europe/London', 'America/New_York', 'UTC'];
const ABSENCE_TYPES = ['DAY_OFF', 'VACATION', 'PARTIAL'];

const EMPTY_ABSENCE = { type: 'VACATION', startDate: '', endDate: '', startTime: '', endTime: '', reason: '' };

/**
 * Where a commercial says when they work.
 *
 *  - weekly rules  : the recurring working hours
 *  - absences      : a day off, a holiday range, or part of a single day
 *
 * Both feed the teleprospection calendar directly: a name disappears from a day
 * as soon as it is covered by an absence.
 */
const AvailabilityPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const toMessage = useApiError();

  const [timezone, setTimezone] = useState('Europe/Paris');
  const [rules, setRules] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [absence, setAbsence] = useState(EMPTY_ABSENCE);

  useEffect(() => {
    agendaApi
      .availability(user.id)
      .then((data) => {
        setTimezone(data.timezone ?? 'Europe/Paris');
        setRules(data.rules ?? []);
        setBlocks(data.blocks ?? []);
      })
      .catch((err) => toast.error(toMessage(err)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const addRule = () =>
    setRules((current) => [...current, { weekday: 1, startTime: '09:00', endTime: '12:30', active: true }]);

  const patchRule = (index, part) =>
    setRules((current) => current.map((rule, i) => (i === index ? { ...rule, ...part } : rule)));

  const removeRule = (index) => setRules((current) => current.filter((_, i) => i !== index));

  const saveRules = async () => {
    setBusy(true);
    try {
      await agendaApi.setAvailability(user.id, { timezone, rules });
      toast.success(t('common.save'));
    } catch (err) {
      toast.error(toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const addAbsence = async () => {
    if (!absence.startDate) return;
    try {
      const payload = {
        type: absence.type,
        startDate: absence.startDate,
        // A day off and a partial day are a single date; a holiday is a range.
        endDate: absence.type === 'VACATION' ? absence.endDate || absence.startDate : absence.startDate,
        reason: absence.reason,
        ...(absence.type === 'PARTIAL' ? { startTime: absence.startTime, endTime: absence.endTime } : {}),
      };
      const updated = await agendaApi.addBlock(user.id, payload);
      setBlocks(updated.blocks ?? []);
      setAbsence(EMPTY_ABSENCE);
      toast.success(t('agenda.addAbsence'));
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const removeAbsence = async (blockId) => {
    try {
      const updated = await agendaApi.removeBlock(user.id, blockId);
      setBlocks(updated.blocks ?? []);
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  if (loading) return <Spinner full />;

  const locale = i18n.resolvedLanguage;
  const sortedBlocks = [...blocks].sort((a, b) =>
    (a.startDate ?? a.date ?? '') < (b.startDate ?? b.date ?? '') ? -1 : 1);

  return (
    <>
      <PageHeader title={t('nav.availability')} />

      {/* ---- Recurring working hours ---- */}
      <Card
        title={t('agenda.workingHours')}
        actions={
          <div className="header-actions">
            <button type="button" className="btn btn--small" onClick={addRule}>
              {t('agenda.addSlotRule')}
            </button>
            <button type="button" className="btn btn--small btn--primary" onClick={saveRules} disabled={busy}>
              {busy ? t('common.saving') : t('common.save')}
            </button>
          </div>
        }
      >
        <Field label={t('settings.timezone')} htmlFor="tz">
          <Select
            id="tz"
            value={timezone}
            onChange={setTimezone}
            options={TIMEZONES.map((zone) => ({ value: zone, label: zone }))}
          />
        </Field>

        <div className="rule-list">
          {rules.map((rule, index) => (
            <div key={`${rule.weekday}-${index}`} className="rule-row">
              <Select
                value={String(rule.weekday)}
                onChange={(value) => patchRule(index, { weekday: Number(value) })}
                options={WEEKDAYS.map((day) => ({ value: String(day), label: t(`agenda.weekdays.${day}`) }))}
              />
              <TextInput type="time" value={rule.startTime} onChange={(value) => patchRule(index, { startTime: value })} />
              <TextInput type="time" value={rule.endTime} onChange={(value) => patchRule(index, { endTime: value })} />
              <label className="inline-check">
                <input
                  type="checkbox"
                  checked={rule.active !== false}
                  onChange={(event) => patchRule(index, { active: event.target.checked })}
                />
                <span>{t('users.statusActive')}</span>
              </label>
              <button type="button" className="btn btn--ghost btn--small" onClick={() => removeRule(index)}>
                {t('common.delete')}
              </button>
            </div>
          ))}
          {!rules.length ? <p className="muted">{t('common.noData')}</p> : null}
        </div>
      </Card>

      {/* ---- Days off and holidays ---- */}
      <Card title={t('agenda.absences')}>
        <div className="rule-row">
          <Select
            value={absence.type}
            onChange={(value) => setAbsence({ ...absence, type: value })}
            options={ABSENCE_TYPES.map((type) => ({ value: type, label: t(`agenda.absenceTypes.${type}`) }))}
          />
          <TextInput
            type="date"
            value={absence.startDate}
            min={isoDay()}
            onChange={(value) => setAbsence({ ...absence, startDate: value })}
            aria-label={t('agenda.absenceFrom')}
          />
          {absence.type === 'VACATION' ? (
            <TextInput
              type="date"
              value={absence.endDate}
              min={absence.startDate || isoDay()}
              onChange={(value) => setAbsence({ ...absence, endDate: value })}
              aria-label={t('agenda.absenceTo')}
            />
          ) : null}
          {absence.type === 'PARTIAL' ? (
            <>
              <TextInput
                type="time"
                value={absence.startTime}
                onChange={(value) => setAbsence({ ...absence, startTime: value })}
              />
              <TextInput
                type="time"
                value={absence.endTime}
                onChange={(value) => setAbsence({ ...absence, endTime: value })}
              />
            </>
          ) : null}
          <TextInput
            value={absence.reason}
            onChange={(value) => setAbsence({ ...absence, reason: value })}
            placeholder={t('agenda.absenceReason')}
          />
          <button type="button" className="btn btn--small" onClick={addAbsence} disabled={!absence.startDate}>
            {t('agenda.addAbsence')}
          </button>
        </div>

        <div className="absence-list">
          {sortedBlocks.map((block) => {
            const start = block.startDate ?? block.date;
            const end = block.endDate ?? start;
            return (
              <div key={block.id} className="absence">
                <Badge value={block.type ?? 'DAY_OFF'} label={t(`agenda.absenceTypes.${block.type ?? 'DAY_OFF'}`)} />
                <span className="absence__dates">
                  {formatDate(start, locale)}
                  {end && end !== start ? ` - ${formatDate(end, locale)}` : ''}
                  {block.type === 'PARTIAL' && block.startTime ? ` (${block.startTime} - ${block.endTime})` : ''}
                </span>
                <span className="absence__reason">{block.reason || ''}</span>
                <button type="button" className="btn btn--ghost btn--small" onClick={() => removeAbsence(block.id)}>
                  {t('common.delete')}
                </button>
              </div>
            );
          })}
          {!sortedBlocks.length ? <p className="muted">{t('agenda.noAbsence')}</p> : null}
        </div>
      </Card>
    </>
  );
};

export default AvailabilityPage;
